import { Box, Button, CircularProgress, Paper, Typography } from "@material-ui/core";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  CheckCircleOutline,
  ErrorOutline,
  HighlightOff,
  HourglassEmpty,
} from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { getK2PaymentStatus } from "Clients/ACCB/service";

const CONFIG = {
  success: {
    icon: <CheckCircleOutline style={{ fontSize: 64, color: TEXT.success }} />,
    title: "Payment Successful",
    message: "Your payment has been received. Thank you.",
  },
  failure: {
    icon: <HighlightOff style={{ fontSize: 64, color: TEXT.error }} />,
    title: "Payment Failed",
    message: "Your payment could not be completed. You can try again from your dashboard.",
  },
  error: {
    icon: <ErrorOutline style={{ fontSize: 64, color: TEXT.warning }} />,
    title: "Something went wrong",
    message: "We could not verify this payment. Please contact the centre if you were charged.",
  },
  // Khajane-II: the payer is back from the treasury page, but the payment is
  // only final once the treasury confirms it (checked in the background).
  processing: {
    icon: <HourglassEmpty style={{ fontSize: 64, color: TEXT.warning }} />,
    title: "Confirming your payment",
    message: "We are confirming your payment with the Karnataka treasury (Khajane-II). "
      + "This usually takes under a minute. Please do not pay again.",
  },
  pending: {
    icon: <HourglassEmpty style={{ fontSize: 64, color: TEXT.warning }} />,
    title: "Payment being confirmed",
    message: "The treasury has not confirmed this payment yet. Its status will update on your "
      + "dashboard automatically. Please do not pay again in the meantime.",
  },
};

// How the result page keeps asking about a Khajane-II payment.
const POLL_INTERVAL_MS = 5000;
const POLL_FOR_MS = 3 * 60 * 1000;

export default function PaymentResult() {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const ref = params.get("ref");
  const isK2 = params.get("gateway") === "k2";
  const [status, setStatus] = useState(params.get("status") || "error");
  const [challan, setChallan] = useState(params.get("challan"));
  const [paymentMode, setPaymentMode] = useState("");

  // Khajane-II: poll our backend until the treasury's answer (RCT034) is in.
  useEffect(() => {
    if (!isK2 || !ref || status !== "processing") return undefined;
    let stopped = false;
    const startedAt = Date.now();
    let timer = null;

    const check = () => {
      getK2PaymentStatus(ref)
        .then((res) => {
          if (stopped) return;
          const data = res.data || {};
          if (data.challan) setChallan(data.challan);
          if (data.payment_mode) setPaymentMode(data.payment_mode);
          if (data.result === "success" || data.result === "failure") {
            setStatus(data.result);
            stopped = true;
          }
        })
        .catch(() => {}) // keep trying; a single failed check is not an answer
        .finally(() => {
          if (stopped) return;
          if (Date.now() - startedAt >= POLL_FOR_MS) {
            setStatus("pending");
            return;
          }
          timer = setTimeout(check, POLL_INTERVAL_MS);
        });
    };
    check();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [isK2, ref, status]);

  const cfg = CONFIG[status] || CONFIG.error;

  return (
    <Box display="flex" justifyContent="center" style={{ padding: "4rem 1rem" }}>
      <Paper style={{ padding: "2.5rem", maxWidth: 460, textAlign: "center", border: "1px solid #E5E5E5" }}>
        {cfg.icon}
        <Typography style={{ fontSize: "1.4rem", fontWeight: 700, marginTop: "1rem" }}>
          {cfg.title}
        </Typography>
        <Typography style={{ color: TEXT.secondary, marginTop: "0.5rem" }}>
          {cfg.message}
        </Typography>
        {status === "processing" && (
          <Box marginTop="1rem"><CircularProgress size={24} /></Box>
        )}
        {challan && (
          <Typography style={{ color: TEXT.muted, fontSize: "0.85rem", marginTop: "0.75rem" }}>
            Challan: {challan}
          </Typography>
        )}
        {isK2 && ref && (
          <Typography style={{ color: TEXT.muted, fontSize: "0.85rem", marginTop: "0.25rem" }}>
            Khajane-II reference: {ref}{paymentMode ? ` · ${paymentMode}` : ""}
          </Typography>
        )}
        <Box height="1.5rem" />
        <Button variant="contained" color="primary" onClick={() => history.push("/")}
                style={{ textTransform: "none" }}>
          Back to Dashboard
        </Button>
      </Paper>
    </Box>
  );
}
